import React, { useState } from 'react';
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';

import { Button } from 'shadcn/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { BulkUploadDropzone } from 'shared/components/bulkUpload';
import { downloadCSV, escapeCSVValue } from 'shared/components/csvDownload';
import { Notifications } from 'shared/components/Notifications';
import { PaginationControls } from 'shared/components/pagination';
import { useSubmitPrivacyRequestCsvMutation } from 'shared/hooks/customer/useSubmitPrivacyRequestCsvMutation';

const COLUMN_DEFS = [
    { header: 'Email', accessorKey: 'email' },
    { header: 'Status', accessorKey: 'status' },
    { header: 'Variant', accessorKey: 'variant' },
    { header: 'Membership Type', accessorKey: 'type_of_paid_membership' },
    { header: 'Start Date', accessorKey: 'start_date' },
    { header: 'End Date', accessorKey: 'end_date' },
    { header: 'Auto-Renew', accessorKey: 'is_auto_renew' },
    { header: 'Membership Status', accessorKey: 'paid_membership_status' },
];

const CSV_HEADERS = COLUMN_DEFS.map((col) => col.header);
const CSV_ACCESSORS = COLUMN_DEFS.map((col) => col.accessorKey);

function downloadResultsAsCsv(results) {
    const rows = [
        CSV_HEADERS.map((h) => escapeCSVValue(h)).join(','),
        ...results.map((row) => CSV_ACCESSORS.map((key) => escapeCSVValue(row[key])).join(',')),
    ];
    downloadCSV(rows.join('\r\n'), 'privacy_requests.csv');
}

function PrivacyRequests() {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [results, setResults] = useState(null);
    const [file, setFile] = useState(null);
    const [uploadKey, setUploadKey] = useState(0);

    const mutation = useSubmitPrivacyRequestCsvMutation();

    const table = useReactTable({
        data: results ?? [],
        columns: COLUMN_DEFS,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 20 } },
    });

    const handleSubmit = () => {
        setResults(null);
        mutation.mutate(file, {
            onSuccess: (res) => {
                if (!res.status) {
                    setNotification({ text: res.msg, level: 'error' });
                    return;
                }
                setNotification({ text: null, level: null });
                setResults(res.data.results);
                setFile(null);
                setUploadKey((k) => k + 1);
            },
        });
    };

    return (
        <div className="flex min-h-screen flex-col">
            <div className="px-4 pt-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-semibold">Privacy Requests</h1>
                </div>
                <Notifications options={notification} />
                <div className="mt-4 mb-4 rounded-lg border bg-white p-5 shadow-sm">
                    <p className="mb-4 text-sm text-muted-foreground">
                        Upload a CSV of customer emails (required header:{' '}
                        <code className="font-mono font-medium">email</code>) to retrieve their privacy request data.
                    </p>
                    <div className="flex flex-col gap-4">
                        <BulkUploadDropzone
                            key={uploadKey}
                            onFileSelect={setFile}
                            isUploading={mutation.isPending}
                            accept=".csv"
                            className="[&>div]:py-4 [&>div]:gap-2"
                        />
                        <div className="flex justify-end">
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleSubmit}
                                disabled={mutation.isPending || !file}
                            >
                                {mutation.isPending ? 'Submitting…' : 'Submit'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {results !== null ? (
                <div className="flex flex-1 flex-col">
                    <div className="py-6">
                        {results.length > 0 ? (
                            <div className="mb-3 flex justify-end px-4">
                                <Button type="button" size="sm" onClick={() => downloadResultsAsCsv(results)}>
                                    Download Data
                                </Button>
                            </div>
                        ) : null}
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id} className="whitespace-normal break-words">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell
                                                    key={cell.id}
                                                    className="whitespace-normal break-words text-left"
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={COLUMN_DEFS.length} className="h-24 text-center">
                                            No results.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {results.length > 0 && table.getPageCount() > 1 ? (
                            <PaginationControls
                                hasPrevious={table.getCanPreviousPage()}
                                hasNext={table.getCanNextPage()}
                                onPrevious={() => table.previousPage()}
                                onNext={() => table.nextPage()}
                                currentPage={table.getState().pagination.pageIndex + 1}
                                totalPages={table.getPageCount()}
                            />
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export { PrivacyRequests };
